import React, { useRef, useState } from "react";
import {
  BookOpen,
  Clock,
  UserCheck,
  FileText,
  Video,
  Users,
  CheckCircle,
  Headphones,
  Mic,
  ChevronDown,
  Award,
  Target,
} from "lucide-react";
import CountUp from "react-countup";
import { motion } from "framer-motion";
import ContactForm from "../components/ContactForm";
import GoogleReviews from "../components/GoogleReviews";

/* ----------------------------- Data & Content ----------------------------- */
const MASTERCLASSES = [
  { test: "GMAT Quant Masterclass", date: "10 Sep", time: "10:00am - 12:00pm", topic: "Problem Solving & Data Sufficiency Techniques" },
  { test: "GMAT Verbal Masterclass", date: "12 Sep", time: "04:00pm - 06:00pm", topic: "Critical Reasoning & Sentence Correction Tips" },
  { test: "GMAT AWA & IR Masterclass", date: "14 Sep", time: "10:00am - 11:30am", topic: "Analytical Writing & Integrated Reasoning Strategies" },
];

const FEATURES = [
  { icon: BookOpen, title: "Complete GMAT Coverage", desc: "Quant, Verbal, AWA, and IR modules." },
  { icon: Clock, title: "Recorded Sessions", desc: "Access lectures anytime, anywhere." },
  { icon: UserCheck, title: "1:1 Mentorship", desc: "Personalized guidance with GMAT experts." },
  { icon: FileText, title: "Mock Test Analysis", desc: "Detailed reports with improvement plans." },
];

const PREP_TIPS = [
  "Strengthen fundamental math and grammar skills.",
  "Attempt at least 2 full-length mock tests every week.",
  "Analyze errors carefully to identify weak areas.",
  "Practice time management and pacing strategies.",
  "Review official GMAT materials for accuracy and style."
];

const TOP_BUSINESS_SCHOOLS = [
  "Harvard Business School",
  "Stanford GSB",
  "Wharton School",
  "INSEAD",
  "London Business School",
  "MIT Sloan"
];

const ONLINE_SESSIONS = [
  { icon: Video, title: "Live Online Classes", desc: "Interactive GMAT prep with real-time feedback." },
  { icon: Users, title: "Small Batch Size", desc: "Focused attention & personalized learning." },
  { icon: Clock, title: "Flexible Schedule", desc: "Attend live or watch recordings later." },
];

const FAQS = [
  { q: "What is the GMAT used for?", a: "The GMAT is used for admission to business schools globally." },
  { q: "How is the GMAT scored?", a: "Scores range from 200–800 for Quant + Verbal; AWA scored 0–6; IR scored 1–8." },
  { q: "Is the GMAT online or in-person?", a: "Both computer-based test and online versions are available." },
  { q: "How often can I take the GMAT?", a: "You can take it once every 16 days, up to 5 times a year." },
  { q: "What is a good GMAT score?", a: "Above 700 is competitive for top MBA programs." },
  { q: "Do you provide practice tests?", a: "Yes — multiple full-length mocks with analysis and score reports." },
];

/* ------------------------------ Motion helpers --------------------------- */
const fadeUp = { hidden: { opacity: 0, y: 28 }, visible: { opacity: 1, y: 0, transition: { duration: 0.6 } } };

/* ------------------------------- Pricing -------------------------------- */
const pricingPlans = [
  {
    title: "Regular", price: "₹20,000", duration: "8 weeks", popular: false,
    features: ["Daily 2 Hours Lecture","2 Problem-Solving Sessions / Week","15 Full-Length GMAT Mocks","Official GMAT Materials","Quant + Verbal + AWA + IR Coverage","Online Support"],
    gradient: "linear-gradient(90deg,#6A85B6,#A67C52)",
  },
  {
    title: "Fast track", price: "₹14,000", duration: "4 weeks", popular: true,
    features: ["1:1 Exclusive Coaching","Daily Intensive Classes","25 Full-Length GMAT Mocks","Application Guidance","Free Counseling Session","Intensive Prep"],
    gradient: "linear-gradient(90deg,#003366,#6A85B6)",
  },
  {
    title: "Crash Course", price: "₹9,500", duration: "2 weeks", popular: false,
    features: ["Quick Quant + Verbal Strategy Sessions","Focused Mocks","Targeted Drills","Study Plan Included","Rapid Revision"],
    gradient: "linear-gradient(90deg,#A67C52,#6A85B6)",
  },
];

/* ------------------------------- Component ------------------------------- */
const GMATPage = () => {
  const contactRef = useRef(null);
  const [faqOpen, setFaqOpen] = useState(null);

  const scrollToContact = () => { contactRef.current?.scrollIntoView({ behavior: "smooth" }); };

  return (
    <div className="bg-[#ECECEC] text-[#003366] min-h-screen relative">

      {/* Floating CTA */}
      <button
        onClick={scrollToContact}
        className="fixed bottom-6 left-6 bg-[#A67C52] text-white px-5 py-3 rounded-full shadow-lg hover:brightness-95 transition z-50"
      >
        Book Free Demo
      </button>

      {/* HERO */}
      <section className="relative overflow-hidden" style={{ background: "linear-gradient(180deg,#003366 0%, #6A85B6 50%, #ECECEC 100%)", color: "#fff" }}>
        <div className="max-w-6xl mx-auto px-6 py-24 text-center">
          <motion.h1 initial="hidden" whileInView="visible" variants={fadeUp} viewport={{ once: true }} className="text-4xl md:text-6xl font-extrabold leading-tight">
            Ace the GMAT — <span style={{ color: "#ECECEC" }}>Target 700+</span> with Expert Coaching
          </motion.h1>
          <motion.p initial="hidden" whileInView="visible" variants={fadeUp} className="mt-6 text-lg md:text-xl max-w-3xl mx-auto opacity-95">
            Learn proven strategies, attempt realistic mocks, and get one-on-one feedback to maximize your GMAT score.
          </motion.p>

          <motion.div initial="hidden" whileInView="visible" variants={fadeUp} className="mt-8 flex justify-center gap-4 flex-wrap">
            <span className="bg-white text-[#003366] px-4 py-1 rounded-full font-semibold">Live Online Classes</span>
            <span className="bg-white text-[#003366] px-4 py-1 rounded-full font-semibold">Expert Mentorship</span>
            <span className="bg-white text-[#003366] px-4 py-1 rounded-full font-semibold">Flexible Schedule</span>
          </motion.div>

          <motion.div initial="hidden" whileInView="visible" variants={fadeUp} className="mt-8">
            <button onClick={scrollToContact} className="px-6 py-3 font-semibold rounded-lg shadow-lg" style={{ background: "#A67C52", color: "#fff" }}>Book Free Demo</button>
          </motion.div>
        </div>
      </section>

      {/* Masterclass Schedule */}
      <section className="bg-[#ECECEC] py-12 px-6">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl font-bold text-center mb-8">Free Masterclass Schedule</h2>
          <div className="grid gap-6">
            {MASTERCLASSES.map((mc, idx) => (
              <motion.div key={idx} whileHover={{ scale: 1.02 }} className="flex flex-col md:flex-row items-start md:items-center bg-white rounded-xl shadow-lg border-l-8 border-[#6A85B6] p-6 transition">
                <div className="flex-1">
                  <h3 className="text-xl font-bold text-[#003366]">{mc.test}</h3>
                  <p className="text-[#6A85B6] mt-1">{mc.date} | {mc.time}</p>
                  <p className="text-[#003366] mt-2 font-semibold">{mc.topic}</p>
                </div>
                <div className="mt-4 md:mt-0 md:ml-6 flex items-center gap-4">
                  <span className="px-3 py-1 bg-[#003366] text-white rounded-full text-sm font-semibold">Seats Limited</span>
                  <button onClick={scrollToContact} className="px-5 py-2 text-white rounded-lg font-semibold transition" style={{ background: "#A67C52" }}>Enroll Now</button>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* GMAT Structure & Features */}
      <section className="max-w-6xl mx-auto px-6 py-12 grid lg:grid-cols-2 gap-8 items-start">
        <motion.div initial="hidden" whileInView="visible" variants={fadeUp} className="bg-white rounded-2xl shadow p-6">
          <h3 className="text-2xl font-bold mb-4 text-[#003366]">GMAT Test Structure</h3>
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-[#ECECEC] p-4 rounded-lg text-center">
              <BookOpen className="mx-auto w-8 h-8 text-[#A67C52]" />
              <h4 className="mt-2 font-semibold text-[#003366]">Verbal</h4>
              <p className="text-sm text-[#6A85B6]">65 mins • 36 Qs</p>
            </div>
            <div className="bg-[#ECECEC] p-4 rounded-lg text-center">
              <Target className="mx-auto w-8 h-8 text-[#6A85B6]" />
              <h4 className="mt-2 font-semibold text-[#003366]">Quant</h4>
              <p className="text-sm text-[#6A85B6]">62 mins • 31 Qs</p>
            </div>
            <div className="bg-[#ECECEC] p-4 rounded-lg text-center">
              <FileText className="mx-auto w-8 h-8 text-[#003366]" />
              <h4 className="mt-2 font-semibold text-[#003366]">AWA</h4>
              <p className="text-sm text-[#6A85B6]">1 essay • 30 mins</p>
            </div>
            <div className="bg-[#ECECEC] p-4 rounded-lg text-center">
              <Clock className="mx-auto w-8 h-8 text-[#A67C52]" />
              <h4 className="mt-2 font-semibold text-[#003366]">Integrated Reasoning</h4>
              <p className="text-sm text-[#6A85B6]">12 questions • 30 mins</p>
            </div>
          </div>
        </motion.div>

        <motion.div initial="hidden" whileInView="visible" variants={fadeUp} className="grid gap-4">
          <h3 className="text-2xl font-bold mb-2 text-[#003366]">What Makes Us Different</h3>
          <div className="grid md:grid-cols-2 gap-4">
            {FEATURES.map((f, idx) => {
              const Icon = f.icon;
              return (
                <motion.div key={idx} whileHover={{ scale: 1.02 }} className="flex items-start bg-white p-4 rounded-lg shadow">
                  <Icon className="w-10 h-10 mr-4 text-[#003366]" />
                  <div>
                    <h4 className="font-semibold text-[#003366]">{f.title}</h4>
                    <p className="text-sm text-[#6A85B6]">{f.desc}</p>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </motion.div>
      </section>

      {/* Prep Tips */}
      <section className="py-12 px-6 bg-[#ECECEC]">
        <div className="max-w-4xl mx-auto text-center">
          <h3 className="text-3xl font-bold text-[#003366] mb-6">Top GMAT Prep Tips</h3>
          <ul className="grid md:grid-cols-2 gap-4 list-disc list-inside text-[#6A85B6] text-left">
            {PREP_TIPS.map((tip, idx) => <li key={idx}>{tip}</li>)}
          </ul>
        </div>
      </section>

      {/* GMAT Score Guide */}
<section className="py-12 px-6 bg-[#ECECEC]">
  <div className="max-w-4xl mx-auto text-center">
    <h3 className="text-3xl font-bold text-[#003366] mb-6">GMAT Score Guide for Top Schools</h3>
    <div className="overflow-x-auto">
      <table className="min-w-full bg-white rounded-xl shadow text-left">
        <thead>
          <tr className="bg-[#003366] text-white">
            <th className="px-4 py-2">Business School</th>
            <th className="px-4 py-2">Average GMAT Score</th>
            <th className="px-4 py-2">Acceptance Rate</th>
          </tr>
        </thead>
        <tbody>
          {TOP_BUSINESS_SCHOOLS.map((school, i) => (
            <tr key={i} className="border-b border-[#ECECEC]">
              <td className="px-4 py-2">{school}</td>
              <td className="px-4 py-2">{i % 2 === 0 ? "720" : "710"}</td>
              <td className="px-4 py-2">{i % 2 === 0 ? "11%" : "14%"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
</section>

{/* GMAT Syllabus */}
<section className="py-12 px-6 bg-[#ECECEC]">
  <div className="max-w-6xl mx-auto text-center">
    <h3 className="text-3xl font-bold text-[#003366] mb-6">Detailed GMAT Syllabus</h3>
    <div className="grid md:grid-cols-2 gap-6 text-left">
      <div className="bg-white p-6 rounded-xl shadow">
        <h4 className="font-semibold text-[#003366] mb-2">Quantitative Section</h4>
        <ul className="list-disc list-inside text-[#6A85B6]">
          <li>Problem Solving: Arithmetic, Algebra, Geometry, Word Problems</li>
          <li>Data Sufficiency: Evaluate the sufficiency of information</li>
          <li>Advanced Topics: Combinatorics, Probability, Statistics</li>
        </ul>
      </div>
      <div className="bg-white p-6 rounded-xl shadow">
        <h4 className="font-semibold text-[#003366] mb-2">Verbal Section</h4>
        <ul className="list-disc list-inside text-[#6A85B6]">
          <li>Reading Comprehension: Long passages, critical analysis</li>
          <li>Critical Reasoning: Strengthen/Weaken arguments, assumptions</li>
          <li>Sentence Correction: Grammar, syntax, conciseness</li>
        </ul>
      </div>
      <div className="bg-white p-6 rounded-xl shadow">
        <h4 className="font-semibold text-[#003366] mb-2">AWA Section</h4>
        <ul className="list-disc list-inside text-[#6A85B6]">
          <li>Analysis of Argument Essay</li>
          <li>Structuring arguments effectively</li>
          <li>Grammar and coherence</li>
        </ul>
      </div>
      <div className="bg-white p-6 rounded-xl shadow">
        <h4 className="font-semibold text-[#003366] mb-2">Integrated Reasoning</h4>
        <ul className="list-disc list-inside text-[#6A85B6]">
          <li>Multi-source reasoning</li>
          <li>Table analysis & graphics interpretation</li>
          <li>Two-part analysis problems</li>
        </ul>
      </div>
    </div>
  </div>
</section>

{/* GMAT Prep Timeline */}
<section className="py-12 px-6 bg-white">
  <div className="max-w-6xl mx-auto text-center">
    <h3 className="text-3xl font-bold text-[#003366] mb-6">GMAT Preparation Timeline</h3>
    <div className="grid md:grid-cols-4 gap-4">
      {["Month 1: Basics", "Month 2: Strategy", "Month 3: Mocks", "Month 4: Revision"].map((phase, i) => (
        <div key={i} className="bg-[#ECECEC] p-6 rounded-xl shadow">
          <h4 className="font-semibold text-[#003366]">{phase}</h4>
          <p className="text-[#6A85B6] mt-2 text-sm">Focus on core topics, practice sessions, and gradual improvement.</p>
        </div>
      ))}
    </div>
  </div>
</section>


      {/* Top Business Schools */}
      <section className="py-12 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <h3 className="text-3xl font-bold text-[#003366] mb-6">Top Business Schools</h3>
          <div className="flex flex-wrap justify-center gap-4">
            {TOP_BUSINESS_SCHOOLS.map((u, i) => (
              <span key={i} className="bg-[#A67C52] text-white px-4 py-2 rounded-full font-semibold">{u}</span>
            ))}
          </div>
        </div>
      </section>

      {/* Online Sessions */}
      <section className="py-12 px-6">
        <div className="max-w-5xl mx-auto text-center mb-8">
          <h2 className="text-3xl font-bold text-[#003366]">Our Online Sessions</h2>
        </div>
        <div className="max-w-5xl mx-auto grid md:grid-cols-3 gap-6">
          {ONLINE_SESSIONS.map((s, i) => {
            const Icon = s.icon;
            return (
              <motion.div key={i} whileHover={{ scale: 1.03 }} className="bg-white rounded-lg p-6 shadow text-center">
                <Icon className="w-12 h-12 mx-auto mb-3 text-[#A67C52]" />
                <h4 className="font-semibold text-[#003366]">{s.title}</h4>
                <p className="text-sm text-[#6A85B6] mt-2">{s.desc}</p>
              </motion.div>
            );
          })}
        </div>
      </section>

      {/* Achievement Counters */}
      <section className="py-12 px-6 bg-[#ECECEC]">
        <div className="max-w-4xl mx-auto text-center">
          <h3 className="text-3xl font-bold text-[#003366] mb-6">Our Achievement in Numbers</h3>
          <div className="flex justify-center gap-12 flex-wrap">
            <div>
              <span className="text-4xl font-bold text-[#003366]">
                <CountUp end={4000} duration={2.5} separator="," />
              </span>
              <p className="text-[#6A85B6]">GMAT Aspirants Trained</p>
            </div>
            <div>
              <span className="text-4xl font-bold text-[#003366]">
                <CountUp end={85} duration={2} suffix="%" />
              </span>
              <p className="text-[#6A85B6]">Score Improvement Rate</p>
            </div>
            <div>
              <span className="text-4xl font-bold text-[#003366]">
                <CountUp end={1200} duration={2} />
              </span>
              <p className="text-[#6A85B6]">Top Scorers</p>
            </div>
          </div>
        </div>
      </section>

      {/* Success Stories */}
      <section className="bg-white py-20 px-4">
        <div className="max-w-4xl mx-auto">
          <motion.h2
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-4xl font-bold mb-16 text-center"
          >
            Success Stories
          </motion.h2>
          <GoogleReviews />
        </div>
      </section>

      {/* FAQ */}
      <section className="py-12 px-6 bg-white">
        <div className="max-w-5xl mx-auto">
          <h3 className="text-3xl font-bold text-[#003366] text-center mb-6">Frequently Asked Questions</h3>
          <div className="space-y-4">
            {FAQS.map((f, i) => {
              const open = faqOpen === i;
              return (
                <div
                  key={i}
                  className="bg-[#ECECEC] p-4 rounded-lg shadow-sm cursor-pointer border border-[#6A85B6]/10"
                  onClick={() => setFaqOpen(open ? null : i)}
                >
                  <div className="flex justify-between items-center">
                    <h4 className="font-semibold text-[#003366]">{f.q}</h4>
                    <ChevronDown className={`text-[#A67C52] transition-transform ${open ? "rotate-180" : ""}`} />
                  </div>
                  {open && <p className="mt-3 text-[#6A85B6]">{f.a}</p>}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Contact Form */}
      <section ref={contactRef}>
        <ContactForm />
      </section>
    </div>
  );
};

export default GMATPage;
